/**
 * LoginBanner — public welcome/migration banner shown at the top of
 * the auth page. Reads its config from `GET /api/config/login-banner`
 * (anonymous, no token needed) and renders nothing when disabled.
 *
 * Dismissed state persists in localStorage keyed on (title|body) so
 * editing the banner copy re-shows it to everyone — admins don't
 * have to push a new "version" string when they tweak the message.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { Info, AlertTriangle, CheckCircle2, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const VARIANT_STYLES = {
  info:    { wrap: 'bg-cyan-50    border-cyan-200    text-cyan-900',    iconClr: 'text-cyan-600',    Icon: Info },
  success: { wrap: 'bg-emerald-50 border-emerald-200 text-emerald-900', iconClr: 'text-emerald-600', Icon: CheckCircle2 },
  warning: { wrap: 'bg-amber-50   border-amber-200   text-amber-900',   iconClr: 'text-amber-600',   Icon: AlertTriangle },
};

export default function LoginBanner() {
  const [cfg, setCfg] = useState(null);
  // `locallyDismissed` only flips when the user clicks the ✕ in THIS
  // session. The persisted dismissal lives in localStorage and is
  // read synchronously below (no effect needed) so we can't trigger
  // `set-state-in-effect`.
  const [locallyDismissed, setLocallyDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/config/login-banner`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (!cancelled) setCfg(data); })
      .catch(() => { /* silent — banner is non-critical UX */ });
    return () => { cancelled = true; };
  }, []);

  // Hash of (title|body) — when the admin edits the message the key
  // changes and previously-dismissed users see the new copy.
  const dismissKey = useMemo(() => {
    if (!cfg) return null;
    const sig = `${cfg.title || ''}|${cfg.body || ''}`;
    let h = 0;
    for (let i = 0; i < sig.length; i++) h = ((h << 5) - h + sig.charCodeAt(i)) | 0;
    return `auto-remap.login-banner-dismissed.${h}`;
  }, [cfg]);

  // Read persisted dismissal synchronously each render — cheap (one
  // localStorage lookup) and avoids the React-19 "set state in effect"
  // anti-pattern.
  const persistedDismissed = useMemo(() => {
    if (!dismissKey) return false;
    try { return localStorage.getItem(dismissKey) === '1'; }
    catch (_e) { return false; }
  }, [dismissKey]);

  if (!cfg || !cfg.enabled) return null;
  if (!cfg.title && !cfg.body) return null;
  if (locallyDismissed || persistedDismissed) return null;

  const style = VARIANT_STYLES[cfg.variant] || VARIANT_STYLES.info;
  const Icon = style.Icon;

  const onDismiss = () => {
    try { if (dismissKey) localStorage.setItem(dismissKey, '1'); }
    catch (_e) { /* localStorage disabled (private mode / iframe) — UI-only dismiss */ }
    setLocallyDismissed(true);
  };

  return (
    <div
      className={`mb-5 p-4 border rounded-xl shadow-sm ${style.wrap}`}
      data-testid="login-banner"
      role="status"
    >
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${style.iconClr}`} />
        <div className="min-w-0 flex-1">
          {cfg.title && (
            <h3 className="font-bold text-sm leading-tight" data-testid="login-banner-title">
              {cfg.title}
            </h3>
          )}
          {cfg.body && (
            <div
              className="text-xs leading-relaxed mt-1 whitespace-pre-line"
              data-testid="login-banner-body"
            >
              {cfg.body}
            </div>
          )}
        </div>
        {cfg.dismissible !== false && (
          <button
            type="button"
            onClick={onDismiss}
            data-testid="login-banner-dismiss"
            className="flex-shrink-0 p-1 rounded hover:bg-black/5 opacity-60 hover:opacity-100"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
